import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./QualityManagementSystem.module.scss";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../../utils/utils";
import {
  setConfluencePage,
  setConfluenceTree,
} from "../../redux/confluenceStore";
import classNames from "classnames";
import { setLoadingContext } from "../../redux/loaderStore";
import _ from "lodash";
import pageNotFoundIllustration from "../../icons/404.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { hasRealContent } from "../../utils/helpers";
import {
  COMPONENT_LOADER,
  CONFLUENCE_TREE_LOADER,
  MODAL_LOADER,
} from "../../constants/loader";
import ConfluencePageDataModal from "../../modals/ConfluencePageDataModal";
import { showModal } from "../../redux/modalStore";
import { setModalConfluencePage } from "../../redux/modalConfluenceStore";
import CustomTable from "../../components/CustomTable/CustomTable";
import Button from "../../components/Button/Button";
import ExpandableMenu from "../../components/ExpandableMenu/ExpandableMenu";
import Input from "../../components/Input/Input";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import useFetchOrganizationData from "../../actions/useFetchOrganizationData";
import { updateFloatingBarVisible } from "../../redux/layoutStore";
import { useTranslation } from "react-i18next";
import {
  ExternalLink,
  File,
  FileText,
  FolderOpen,
  GripVertical,
  RefreshCw,
  Search,
  Target,
} from "lucide-react";

const QualityManagementSystem = ({ getConfluenceTree }) => {
  const { orgId, pageId } = useParams();
  const location = useLocation();
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const leftSideRef = useRef(null);
  const panelGroupRef = useRef(null);

  const hasFetchedPageData = useRef(false); // Ref to control the first fetch

  const fetchingTree = useRef(false);
  const fetchingPage = useRef(false);

  const navigate = useNavigate();

  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isResizingPanels, setIsResizingPanels] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const saved = Number(localStorage.getItem("qms_left_panel_width"));
    return Number.isFinite(saved) && saved > 0 ? saved : 380;
  });
  const leftPanelWidthRef = useRef(leftPanelWidth);

  const confluenceTreeData = useSelector(
    (state) => state.confluence.confluenceTreeData,
  );
  const confluencePageData = useSelector(
    (state) => state.confluence.confluencePageData,
  );
  
  const modalConfluencePageData = useSelector(
    (state) => state.modalConfluence.modalConfluencePageData,
  );
  const module = useSelector((state) => state.user.modules[orgId]);
  const loadingContexts = useSelector((state) => state.loader.loadingContexts);
  const notFoundPage = useSelector((state) => state.layout.notFoundPage);
  const currentOrganization = useSelector(
    (state) => state.user.organizations[orgId],
  );

  const isComponentLoading = loadingContexts[COMPONENT_LOADER];
  const isTreeLoading = loadingContexts[CONFLUENCE_TREE_LOADER];

  // Fetch organization data when navigating through URL
  useFetchOrganizationData();

  useEffect(() => {
    leftPanelWidthRef.current = leftPanelWidth;
  }, [leftPanelWidth]);

  const persistLeftPanelWidth = useCallback((width) => {
    try {
      localStorage.setItem("qms_left_panel_width", String(Math.round(width)));
    } catch {
      // ignore
    }
  }, []);

  const clampLeftPanelWidth = useCallback(
    (proposedWidth) => {
      const MIN_LEFT = 300;
      const MAX_LEFT = 560;
      const MIN_RIGHT = 520;
      const RESIZER_WIDTH = 24;

      const containerWidth =
        panelGroupRef.current?.getBoundingClientRect?.().width ?? null;

      const maxByContainer = containerWidth
        ? Math.max(MIN_LEFT, containerWidth - MIN_RIGHT - RESIZER_WIDTH)
        : MAX_LEFT;

      const max = Math.min(MAX_LEFT, maxByContainer);
      return Math.min(max, Math.max(MIN_LEFT, proposedWidth));
    },
    [],
  );

  useEffect(() => {
    setLeftPanelWidth((currentWidth) => clampLeftPanelWidth(currentWidth));
  }, [clampLeftPanelWidth]);

  useEffect(() => {
    const handleResize = () => {
      setLeftPanelWidth((currentWidth) => clampLeftPanelWidth(currentWidth));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clampLeftPanelWidth]);

  useEffect(() => {
    if (!isResizingPanels) return undefined;

    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isResizingPanels]);

  const handleResizerPointerDown = useCallback(
    (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      event.preventDefault();

      const startX = event.clientX;
      const startWidth = leftPanelWidthRef.current;

      setIsResizingPanels(true);
      event.currentTarget?.setPointerCapture?.(event.pointerId);

      const handlePointerMove = (moveEvent) => {
        const nextWidth = clampLeftPanelWidth(
          startWidth + (moveEvent.clientX - startX),
        );
        leftPanelWidthRef.current = nextWidth;
        setLeftPanelWidth(nextWidth);
      };

      const handlePointerUp = () => {
        setIsResizingPanels(false);
        persistLeftPanelWidth(leftPanelWidthRef.current);
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [clampLeftPanelWidth, persistLeftPanelWidth],
  );

  const handleResizerKeyDown = useCallback(
    (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();

      const delta = event.key === "ArrowLeft" ? -24 : 24;
      setLeftPanelWidth((currentWidth) => {
        const nextWidth = clampLeftPanelWidth(currentWidth + delta);
        persistLeftPanelWidth(nextWidth);
        return nextWidth;
      });
    },
    [clampLeftPanelWidth, persistLeftPanelWidth],
  );

  useEffect(() => {
    dispatch(updateFloatingBarVisible(true));
  }, [dispatch]);

  const clearConfluenceData = useCallback(() => {
    dispatch(setConfluenceTree(null));
    dispatch(setConfluencePage(null));
  }, [dispatch]);

  const sessionToken = localStorage.getItem("session_token");

  // Extract the module path from the URL
  const pathSegments = location.pathname.split("/");
  const modifiedPath = `/${pathSegments[2]}`; // The third segment is the module path

  const extractChapter = (pageName) => {
    const match = pageName.match(/^([A-H]\d*)\s*[-–.]?\s*/);
    return match ? match[1] : null;
  };

  // Find the module object corresponding to the current path
  const currentModule = module?.find((mod) => mod.path === modifiedPath);

  const findNodeById = useCallback((nodes, targetId) => {
    if (!Array.isArray(nodes) || !targetId) return null;
    for (const node of nodes) {
      if (String(node.page_id) === String(targetId)) return node;
      if (node.children?.length) {
        const match = findNodeById(node.children, targetId);
        if (match) return match;
      }
    }
    return null;
  }, []);

  const getConfluencePageData = useCallback(
    async (pageId, pageName) => {
      // Prevent duplicate calls for page data
      if (fetchingPage.current) return;
      fetchingPage.current = true;

      setSelectedPageId(pageId); // Update state immediately
      const chapter = extractChapter(pageName);
      setSelectedChapter(chapter);

      if (!sessionToken) {
        clearConfluenceData();
        fetchingPage.current = false; // Reset fetching flag
        return;
      }

      // Extract the space parameter from the module object
      const space = currentModule?.params?.space || "";

      const options = {
        method: "GET",
        url: `/confluence/page?org_id=${orgId}&space_key=${space}&page_id=${pageId}`,
        headers: {
          "session-token": sessionToken,
        },
      };

      dispatch(
        setLoadingContext({ context: COMPONENT_LOADER, isLoading: true }),
      );

      try {
        const response = await axiosInstance.request(options);
        dispatch(setConfluencePage(response.data));
      } catch (error) {
        console.error("Error fetching page:", error);
        clearConfluenceData();
        dispatch(
          showModal({
            name: "error_modal",
          }),
        );
      } finally {
        dispatch(
          setLoadingContext({ context: COMPONENT_LOADER, isLoading: false }),
        );
        fetchingPage.current = false; // Reset fetching flag
      }
    },
    [clearConfluenceData, currentModule, orgId, sessionToken, dispatch],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!sessionToken) return;

      try {
        // Fetch tree data if not already fetched
        if (!fetchingTree.current && !confluenceTreeData) {
          fetchingTree.current = true; // Mark as fetching
          await getConfluenceTree(`/${orgId}/qms`, sessionToken);
          fetchingTree.current = false; // Reset after fetch
        }

        // Fetch page data when navigating directly by URL
        if (
          confluenceTreeData &&
          pageId &&
          String(selectedPageId) !== String(pageId)
        ) {
          const targetNode = findNodeById(confluenceTreeData?.children, pageId);
          await getConfluencePageData(pageId, targetNode?.page_name || "");
        }
      } catch (error) {
        console.error("Error loading data:", error);
        clearConfluenceData();
        fetchingTree.current = false;
        fetchingPage.current = false;
      }
    };

    fetchData();

    // Reset fetching states when orgId changes
    return () => {
      fetchingTree.current = false;
      fetchingPage.current = false;
    };
  }, [
    orgId,
    pageId,
    sessionToken,
    confluenceTreeData,
    getConfluenceTree,
    findNodeById,
    getConfluencePageData,
    selectedPageId,
    clearConfluenceData,
  ]);

  const getModalConfluencePageData = useCallback((space, pageId) => {
    const options = {
      method: "GET",
      url: `/confluence/page?org_id=${orgId}&space_key=${space}&page_id=${pageId}`,
      headers: {
        "session-token": sessionToken,
      },
    };

    dispatch(setLoadingContext({ context: MODAL_LOADER, isLoading: true }));

    return axiosInstance
      .request(options)
      .then((response) => {
        dispatch(
          setLoadingContext({ context: MODAL_LOADER, isLoading: false }),
        );
        dispatch(setModalConfluencePage(response.data));
      })
      .catch(() => {
        dispatch(
          setLoadingContext({ context: MODAL_LOADER, isLoading: false }),
        );
        dispatch(
          showModal({
            name: "error_modal",
          }),
        );
      });
  }, [dispatch, orgId, sessionToken]);

  const isConfluencePageData = !_.isEmpty(confluencePageData);

  // Auto-select the first page when tree is ready and URL doesn't specify a page.
  useEffect(() => {
    if (!orgId || !sessionToken || !confluenceTreeData) return;
    if (pageId) return;
    if (hasFetchedPageData.current) return;

    const firstNode = confluenceTreeData?.children?.[0];
    if (!firstNode?.page_id) return;

    hasFetchedPageData.current = true;
    navigate(`/${orgId}/qms/${firstNode.page_id}`, { replace: true });
    getConfluencePageData(firstNode.page_id, firstNode.page_name);
  }, [orgId, sessionToken, confluenceTreeData, pageId, navigate, getConfluencePageData]);

  useEffect(() => {
    clearConfluenceData();
    hasFetchedPageData.current = false;
  }, [orgId, clearConfluenceData]);

  useEffect(() => {
    if (confluencePageData?.title) {
      const chapter = extractChapter(confluencePageData.title);
      setSelectedChapter(chapter || null);
    }
  }, [confluencePageData]);

  const isContentEmpty = !hasRealContent(confluencePageData?.body);

  const addOnClickHandlersToLinks = (htmlContent) => {
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(htmlContent, "text/html");

    const links = htmlDoc.querySelectorAll("a");
    const fileExtensions = /\.(pdf|docx|xlsx|csv|pptx|txt|zip)$/i; // Files extensions

    links.forEach((link) => {
      const cSpace = link.getAttribute("c-space");
      const cPageId = link.getAttribute("c-page_id");
      const href = link.getAttribute("href");

      if (href && fileExtensions.test(href)) {
        // Handle file links: open in a new tab
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      } else if (cSpace && cPageId) {
        // Handle links with `c-space` and `c-page_id` (internal links for modal)
        link.setAttribute(
          "onclick",
          `handleLinkClick(event, '${cSpace}', ${cPageId})`,
        );
      } else if (href && href.startsWith("http")) {
        // Handle other external links
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      }
    });

    return htmlDoc.body.innerHTML;
  };

  // Function to handle link clicks
  const handleLinkClick = useCallback((event, cSpace, cPageId) => {
    event.preventDefault();
    getModalConfluencePageData(cSpace, cPageId);
    dispatch(
      showModal({
        name: "quality_management_system_modal",
      }),
    );
  }, [dispatch, getModalConfluencePageData]);

  useEffect(() => {
    window.handleLinkClick = handleLinkClick;
  }, [handleLinkClick]);

  // Handler for opening the "Edit QMS" link in a new tab
  const handleEditQMSClick = () => {
    const space = currentModule?.params?.space || "";
    const targetPageId = selectedPageId || pageId;
    const atlassianDomain = currentOrganization?.AtlassianDomain;

    if (atlassianDomain && space && targetPageId) {
      const editUrl = `https://${atlassianDomain}/wiki/spaces/${space}/pages/${targetPageId}`;
      window.open(editUrl, "_blank");
    } else {
      console.error("Could not construct the Atlassian edit URL.");
    }
  };

  const handleSyncProcedures = async () => {
    if (!sessionToken || isTreeLoading || fetchingTree.current) return;
    try {
      fetchingTree.current = true;
      await getConfluenceTree(`/${orgId}/qms`, sessionToken);
    } finally {
      fetchingTree.current = false;
    }
  };

  const filterTreeData = (treeData, query) => {
    if (!query) return treeData; // Return the original tree data if there's no query

    const result = [];

    const searchTree = (nodes) => {
      nodes.forEach((node) => {
        if (node.page_name.toLowerCase().includes(query.toLowerCase())) {
          result.push(node);
        }
        if (node.children?.length > 0) {
          searchTree(node.children);
        }
      });
    };

    searchTree(treeData);
    return result; // Return only the matching nodes
  };

  const filteredTreeData = filterTreeData(
    confluenceTreeData?.children || [],
    searchQuery,
  );

  const selectedTreeNode = useMemo(
    () => findNodeById(confluenceTreeData?.children, selectedPageId || pageId),
    [confluenceTreeData, findNodeById, pageId, selectedPageId],
  );

  const formatLastEdited = (dateValue) => {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const lastEdited =
    formatLastEdited(confluencePageData?.updated_at) ||
    formatLastEdited(confluencePageData?.updated);

  const PageLoadingSkeleton = () => (
    <div className={styles.rightPanelSkeleton}>
      <div
        className={styles.skeletonBlock}
        style={{ width: "72%", height: 52 }}
      />
      <div
        className={styles.skeletonBlock}
        style={{ width: "94%", height: 18 }}
      />
      <div
        className={styles.skeletonBlock}
        style={{ width: "90%", height: 18 }}
      />
      <div
        className={styles.skeletonBlock}
        style={{ width: "82%", height: 18 }}
      />
      <div
        className={styles.skeletonBlock}
        style={{ width: "68%", height: 18 }}
      />
    </div>
  );

  const PageStructure = ({ nodes, depth = 0 }) => {
    if (!Array.isArray(nodes) || nodes.length === 0) return null;
    return (
      <>
        {nodes.map((node) => {
          const hasChildren = Array.isArray(node.children) && node.children.length > 0;
          return (
            <div className={styles.structureNode} key={node.page_id || node.page_name}>
              <button
                type="button"
                className={styles.structureRow}
                style={{ paddingLeft: `${depth * 12 + 12}px` }}
                onClick={() => {
                  if (!node.page_id) return;
                  navigate(`/${orgId}/qms/${node.page_id}`);
                  getConfluencePageData(node.page_id, node.page_name);
                }}
              >
                <span className={styles.structureIcon}>
                  <File size={16} />
                </span>
                <span className={styles.structureText}>{node.page_name}</span>
              </button>
              {hasChildren && (
                <div className={styles.structureChildren}>
                  <PageStructure nodes={node.children} depth={depth + 1} />
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <MainLayout>
      <div className={styles.column}>
        {notFoundPage ? (
          <div className={styles.pageNotFoundWrapper}>
            <img src={pageNotFoundIllustration} alt="404 Illustration" />
            <span>{t("qualityManagement.notFound.message")}</span>
          </div>
        ) : (
          <>
            <div className={styles.header}>
              <div className={styles.headerText}>
                <h1 className={styles.headerTitle}>
                  <span className={styles.headerTitleIcon} aria-hidden="true">
                    <Target size={28} />
                  </span>
                  {t("modules.qualitymanagementsystem")}
                </h1>
                <p className={styles.headerSubtitle}>
                  {t("qualityManagement.subtitle")}
                </p>
              </div>
              <div className={styles.headerActions}>
                <Button
                  text={t("qualityManagement.syncButton")}
                  className={styles.syncButton}
                  icon={<RefreshCw size={16} />}
                  iconPosition="left"
                  onClick={handleSyncProcedures}
                  disabled={isTreeLoading || !sessionToken}
                />
              </div>
            </div>

            <div className={styles.searchCard}>
              <div className={styles.searchCardTitle}>
                {t("qualityManagement.searchCardTitle")}
              </div>
              <div className={styles.searchRow}>
                <Input
                  wrapperClassName={styles.searchInputWrapper}
                  className={styles.searchInput}
                  placeholder={t("qualityManagement.search.placeholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  isClearable
                  onClear={() => setSearchQuery("")}
                  icon={Search}
                  iconPosition="left"
                  iconStyle={{ width: 18, height: 18, color: "#5D5D6F" }}
                />
                <button
                  type="button"
                  className={styles.searchButton}
                  onClick={() => leftSideRef.current?.scrollTo?.({ top: 0, behavior: "smooth" })}
                  disabled={!searchQuery}
                >
                  {t("qualityManagement.searchButton")}
                </button>
              </div>
            </div>

            <div
              className={styles.panelGroup}
              ref={panelGroupRef}
              style={{ "--qms-left-panel-width": `${leftPanelWidth}px` }}
            >
              <div className={classNames(styles.card, styles.leftPanel)}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitle}>
                    {t("qualityManagement.proceduresTitle")}
                  </div>
                </div>
                <div className={classNames(styles.cardBody, styles.leftPanelBody)} ref={leftSideRef}>
                  {searchQuery ? (
                    filteredTreeData?.length > 0 ? (
                      <div className={styles.results}>
                        {filteredTreeData.map((page) => (
                          <button
                            type="button"
                            key={page.page_id}
                            onClick={() => {
                              navigate(`/${orgId}/qms/${page.page_id}`);
                              getConfluencePageData(page.page_id, page.page_name);
                            }}
                            className={classNames(styles.searchResultItem, {
                              [styles.active]: String(selectedPageId || pageId) === String(page.page_id),
                            })}
                          >
                            <span className={styles.searchResultIcon}>
                              <FileText size={16} />
                            </span>
                            <span className={styles.searchResultText}>
                              {page.page_name}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.noResults}>
                        {t("qualityManagement.search.noResults")}
                      </div>
                    )
                  ) : (
                    <ExpandableMenu
                      treeData={confluenceTreeData?.children || []}
                      onNodeClick={(targetPageId, pageName) => {
                        navigate(`/${orgId}/qms/${targetPageId}`);
                        getConfluencePageData(targetPageId, pageName);
                      }}
                      selectedPageId={selectedPageId || pageId}
                    />
                  )}
                </div>
              </div>

              <div
                className={classNames(
                  styles.panelResizer,
                  isResizingPanels && styles.panelResizerDragging,
                )}
                role="separator"
                aria-orientation="vertical"
                aria-label={t("qualityManagement.resizePanelsLabel")}
                tabIndex={0}
                onPointerDown={handleResizerPointerDown}
                onKeyDown={handleResizerKeyDown}
              >
                <div className={styles.panelResizerHandle} aria-hidden="true">
                  <GripVertical size={16} />
                </div>
              </div>

              <div className={classNames(styles.card, styles.rightPanel)}>
                <div className={styles.pageHeader}>
                  <div className={styles.pageHeaderLeft}>
                    <div className={styles.pageTitle}>
                      {confluencePageData?.title || t("qualityManagement.noPageSelected.title")}
                    </div>
                    {lastEdited && (
                      <div className={styles.pageMeta}>
                        {t("qualityManagement.lastEditedLabel")} {lastEdited}
                      </div>
                    )}
                  </div>
                  <div className={styles.pageHeaderRight}>
                    {confluencePageData?.version && (
                      <div className={styles.pageVersion}>
                        {t("qualityManagement.versionLabel")} {confluencePageData.version}
                      </div>
                    )}
                    <button
                      type="button"
                      className={styles.editProcedureButton}
                      onClick={handleEditQMSClick}
                      disabled={!sessionToken || !(selectedPageId || pageId)}
                    >
                      <ExternalLink size={16} />
                      {t("qualityManagement.editButton")}
                    </button>
                  </div>
                </div>

                <div className={styles.pageBody}>
                  {isComponentLoading && (
                    <div className={styles.loadingWrapper}>
                      <PageLoadingSkeleton />
                    </div>
                  )}

                  {!isComponentLoading && !isConfluencePageData && (
                    <div className={styles.emptyState}>
                      <div className={styles.emptyStateText}>
                        {t("qualityManagement.noPageSelected.description")}
                      </div>
                    </div>
                  )}

                  {!isComponentLoading && isConfluencePageData && (
                    <>
                      {selectedTreeNode?.children?.length > 0 && (
                        <div
                          className={classNames(
                            styles.innerCard,
                            styles.structureCard,
                          )}
                        >
                          <div
                            className={classNames(
                              styles.innerCardHeader,
                              styles.structureCardHeader,
                            )}
                          >
                            <span
                              className={classNames(
                                styles.innerCardTitle,
                                styles.structureCardTitle,
                              )}
                            >
                              <span
                                className={styles.innerCardTitleIcon}
                                aria-hidden="true"
                              >
                                <FolderOpen size={16} />
                              </span>
                              {t("qualityManagement.pageStructureTitle")}
                            </span>
                          </div>
                          <div
                            className={classNames(
                              styles.innerCardBody,
                              styles.structureCardBody,
                            )}
                          >
                            <PageStructure nodes={selectedTreeNode.children} />
                          </div>
                        </div>
                      )}

                      {selectedChapter && (
                        <div className={classNames(styles.innerCard, styles.riskCard)}>
                          <div className={classNames(styles.innerCardHeader, styles.riskCardHeader)}>
                            <span className={classNames(styles.innerCardTitle, styles.riskCardTitle)}>
                              {t("qualityManagement.riskInsightsTitle")}
                            </span>
                          </div>
                          <div className={classNames(styles.innerCardBody, styles.riskCardBody)}>
                            <CustomTable
                              key={selectedPageId || pageId || selectedChapter}
                              selectedChapter={selectedChapter}
                              defaultExpanded
                              className={classNames(isContentEmpty && styles.fullHeight)}
                            />
                          </div>
                        </div>
                      )}

                      {!isContentEmpty && (
                        <div className={styles.contentCard}>
                          <div
                            id="confluence-content"
                            className={styles.confluenceContent}
                            dangerouslySetInnerHTML={{
                              __html: addOnClickHandlersToLinks(confluencePageData?.body),
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        <ConfluencePageDataModal
          modalConfluencePageData={modalConfluencePageData}
          addOnClickHandlersToLinks={addOnClickHandlersToLinks}
        />
      </div>
    </MainLayout>
  );
};

export default QualityManagementSystem;
